// androidBack.ts — 안드로이드 시스템 뒤로 버튼 판정 (순수 함수 · 설치는 components/AndroidBackButton.tsx)
//
// 왜 필요한가 (설계 docs/specs/2026-09-11-android-v5-back-edge-design.md §1):
//   뒤로 리스너가 없으면 @capacitor/app은 canGoBack()일 때만 웹뷰 goBack()을 하고 아니면 아무것도 안 한다.
//   그래서 ① 홈에서 뒤로가 무반응(A-4) ② 탭 루트에서 뒤로가 방문 기록에 남은 로그인 화면으로 갔다(A-3).
//   iOS엔 시스템 뒤로 버튼이 없어 SE3 실기로는 드러나지 않던 결함이다.
//
// 판정 우선순위 (효신님 결정 2026-09-11):
//   ① 닫을 수 있는 오버레이가 열려 있으면 → 그 오버레이만 닫는다
//   ② 홈(/) → 앱 나가기(백그라운드 · 안드로이드 12+ 기본 동작과 같다)
//   ③ 그 밖의 탭 루트 → 홈으로
//   ④ 하위 화면 → 공용 useBackTo() — 헤더 ←·스와이프 뒤로와 같은 함수라 세 경로의 도착지가 같다

import { isTabRoot } from './tabNavigation';

export type AndroidBackAction = 'dismiss' | 'minimize' | 'home' | 'back';

// 닫을 수 있는 오버레이 = 공통 모달 두 종 + 옵트인 속성.
//   - .cs-modal-overlay       : CSModal(cs.jsx — components/cs/cs.js 파생본) 오버레이. onClick={onClose}
//   - .bottom-sheet__backdrop : BottomSheet backdrop. onClick={onClose}
//   - [data-back-dismiss]     : 인라인 전용 오버레이가 뒤로로 닫혀야 할 때 스스로 붙이는 표지(옵트인)
// 공통 컴포넌트는 고치지 않는다 — 오버레이 클릭을 대신 눌러 기존 onClose를 태운다.
//   바깥 클릭으로 안 닫히게 만든 강제 모달은 뒤로로도 안 닫힌다(의도 존중).
export const DISMISSIBLE_OVERLAY_SELECTOR = '.cs-modal-overlay, .bottom-sheet__backdrop, [data-back-dismiss]';

/** 뒤로 버튼을 눌렀을 때 할 일. pathname은 react-router 기준(HashRouter면 해시 뒤 경로). */
export function decideAndroidBack(pathname: string, overlayOpen: boolean): AndroidBackAction {
  if (overlayOpen) return 'dismiss';
  if (pathname === '/') return 'minimize';
  if (isTabRoot(pathname)) return 'home';
  return 'back';
}

/**
 * 맨 위 오버레이 — 문서 순서 마지막. 모달은 모두 body에 포털로 append되므로 나중에 연 것이 뒤에 온다.
 * root를 주입받는 이유: vitest 환경이 node라 가짜 루트로 테스트한다(실행 시엔 document).
 */
export function topDismissibleOverlay(root: Pick<ParentNode, 'querySelectorAll'>): Element | null {
  const list = root.querySelectorAll(DISMISSIBLE_OVERLAY_SELECTOR);
  return list.length ? list[list.length - 1] : null;
}
